import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { attendanceAPI, companyAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { formatTime, formatMinutesToHours } from '../../lib/utils';
import { Clock, LogIn, LogOut, Timer, AlertTriangle, Loader2, MapPin } from 'lucide-react';

const EmployeeDashboard = () => {
  const { user } = useAuth();
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [attRes, compRes] = await Promise.all([
        attendanceAPI.getToday(),
        user?.company_id ? companyAPI.getOne(user.company_id) : Promise.resolve({ data: null })
      ]);
      setTodayAttendance(attRes.data);
      setCompany(compRes.data);
    } catch (err) {
      // Attendance might not exist yet
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      // Get location if available
      let location = null;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000, enableHighAccuracy: true });
          });
          location = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          };
        } catch (e) {
          // Location not available
        }
      }
      
      const response = await attendanceAPI.clockIn({ location });
      setTodayAttendance(response.data);
      toast.success('Clocked in successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      let location = null;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000, enableHighAccuracy: true });
          });
          location = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          };
        } catch (e) {
          // Location not available
        }
      }
      
      const response = await attendanceAPI.clockOut({ location });
      setTodayAttendance(response.data);
      toast.success('Clocked out successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClockingOut(false);
    }
  };

  const now = new Date();
  const currentTime = now.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' });
  const currentDate = now.toLocaleDateString('en-PH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) {
    return (
      <DashboardLayout title="My Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const isClockedIn = todayAttendance?.clock_in && !todayAttendance?.clock_out;
  const isClockedOut = todayAttendance?.clock_out;

  return (
    <DashboardLayout 
      title={`Good ${now.getHours() < 12 ? 'Morning' : now.getHours() < 18 ? 'Afternoon' : 'Evening'}, ${user?.first_name}!`}
      subtitle={currentDate}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Clock In/Out Card */}
        <Card className="lg:col-span-2">
          <CardContent className="p-8">
            <div className="text-center">
              <p className="text-6xl font-bold text-primary mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {currentTime}
              </p>
              
              {!todayAttendance?.clock_in && (
                <Button 
                  size="lg" 
                  className="mt-6 px-12 py-6 text-lg gap-3"
                  onClick={handleClockIn}
                  disabled={clockingIn}
                  data-testid="clock-in-btn"
                >
                  {clockingIn ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <LogIn className="h-6 w-6" />
                  )}
                  Clock In
                </Button>
              )}
              
              {isClockedIn && (
                <Button 
                  size="lg" 
                  variant="destructive"
                  className="mt-6 px-12 py-6 text-lg gap-3"
                  onClick={handleClockOut}
                  disabled={clockingOut}
                  data-testid="clock-out-btn"
                >
                  {clockingOut ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <LogOut className="h-6 w-6" />
                  )}
                  Clock Out
                </Button>
              )}
              
              {isClockedOut && (
                <div className="mt-6">
                  <Badge className="status-approved text-lg px-4 py-2">
                    Day Complete
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Today's Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Today&apos;s Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                <div className="flex items-center gap-2">
                  <LogIn className="h-5 w-5 text-emerald-500" />
                  <span>Clock In</span>
                </div>
                <span className="font-medium">
                  {todayAttendance?.clock_in ? formatTime(todayAttendance.clock_in) : '--:--'}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                <div className="flex items-center gap-2">
                  <LogOut className="h-5 w-5 text-red-500" />
                  <span>Clock Out</span>
                </div>
                <span className="font-medium">
                  {todayAttendance?.clock_out ? formatTime(todayAttendance.clock_out) : '--:--'}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                <div className="flex items-center gap-2">
                  <Timer className="h-5 w-5 text-blue-500" />
                  <span>Worked</span>
                </div>
                <span className="font-medium">
                  {formatMinutesToHours(todayAttendance?.worked_minutes || 0)}
                </span>
              </div>
              
              {todayAttendance?.late_minutes > 0 && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg text-amber-700">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    <span>Late</span>
                  </div>
                  <span className="font-medium">
                    {todayAttendance.late_minutes} mins
                  </span>
                </div>
              )}
              
              {todayAttendance?.overtime_minutes > 0 && (
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg text-purple-700">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    <span>Overtime</span>
                  </div>
                  <span className="font-medium">
                    {formatMinutesToHours(todayAttendance.overtime_minutes)}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Status Badge */}
        <Card>
          <CardHeader>
            <CardTitle>Attendance Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              {!todayAttendance ? (
                <>
                  <Badge className="status-absent text-lg px-6 py-2 mb-4">Not Clocked In</Badge>
                  <p className="text-muted-foreground">Clock in to start your day</p>
                </>
              ) : (
                <>
                  <Badge className={`text-lg px-6 py-2 mb-4 ${
                    todayAttendance.status === 'late' ? 'status-late' : 'status-present'
                  }`}>
                    {todayAttendance.status === 'late' ? 'Late' : 'Present'}
                  </Badge>
                  {todayAttendance.clock_in_ip && (
                    <p className="text-xs text-muted-foreground mt-4">
                      <MapPin className="h-3 w-3 inline mr-1" />
                      IP: {todayAttendance.clock_in_ip}
                    </p>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default EmployeeDashboard;
